/** @jsxImportSource theme-ui */
import { useBreakpointIndex } from '@theme-ui/match-media';
import classNames from 'classnames';
import React, { useState } from 'react';
import Shiitake from 'shiitake';

import theme from 'theme';
import { FlexDirectionProperty, sxtheme } from 'utils/cssHelpers';
import { isMobile } from 'utils/responsive';

import { BdIcon } from 'components/_common/BdIcon';
import { FlexGroup } from 'components/_common/FlexGroup';
import { Typography } from 'components/_common/Typography';
import { IconButton } from 'components/_controls/IconButton';
import { Error } from 'components/Error';
import { Loading } from 'components/Loading';

import { ScrollToTopButton } from '../ScrollToTopButton';

// TODO: this is a replacement for Panel component that decouples the Panel of scrim state.
//       After refactoring work is done, please rename this to Panel and delete the old Panel

export interface ILoadableProps {
  isLoading?: boolean;
  hasError?: boolean;
  onRetryClick?: () => void;
}
export interface IViewPanelProps {
  canClose?: boolean;
  canMinimize?: boolean;
  isMinimized?: boolean;
  onMinimize?: (isMinimized: boolean) => void;
  onClose?: () => void;
}

type ViewPanelProps = ILoadableProps &
  IViewPanelProps & {
    title: string;
    subtitle?: string;
    subtitleMobile?: string;
    headerActions?: React.ReactNode;
    toolbar?: React.ReactNode;
    isSecondary?: boolean | 'show-divider';
    grayBG?: boolean;
    scrollToTopButtonThresh?: number;
    isStandAlone?: boolean;
    isAnimated?: boolean;
    width?: number;
    flexContentDirection?: FlexDirectionProperty; // if set, panel children will be arranged with display: flex
  };

export const ViewPanel: React.FC<ViewPanelProps> = ({
  isLoading = false,
  hasError = false,
  onRetryClick,
  isMinimized,
  title,
  subtitle = undefined,
  subtitleMobile = undefined,
  headerActions = null,
  toolbar = undefined,
  children,
  onClose = null,
  onMinimize,
  canClose = true,
  canMinimize = true,
  isSecondary = false,
  grayBG = false,
  scrollToTopButtonThresh = undefined,
  isStandAlone = true,
  isAnimated = false,
  width = 350,
  flexContentDirection,
  ...props
}) => {
  const handleOnMinimize = () => onMinimize && onMinimize(!isMinimized);
  const appliedWidth = isMinimized ? 41 : width;
  const isMobileDevice = isMobile(useBreakpointIndex());
  const [isScrollToTopVisible, setisScrollToTopVisible] = useState(false);
  const refScrollCon = React.useRef<HTMLDivElement>();

  const hasScrollToTopConfigured = !(
    scrollToTopButtonThresh == null || scrollToTopButtonThresh === undefined
  );

  if (!isStandAlone)
    return (
      <>
        {toolbar && <div sx={{ p: 0 }}>{toolbar}</div>}
        {children}
      </>
    );

  return (
    <div
      className={classNames({
        'bd-animation-sliding-panel': isAnimated,
      })}
      data-testid={`panel-${title}`}
      sx={{
        height: '100%',
        minWidth: ['100%', appliedWidth],
        maxWidth: ['100%', appliedWidth],
        borderRight: ['none', `1px solid ${theme.colors.stone20}`], // CODE: 32b8cfab: border-right for panel separation
        boxSizing: 'content-box',
        bg: sxtheme(t => (isSecondary && !isMobileDevice ? t.colors.deepSea10 : t.colors.white)),
        ':last-child': {
          borderRight: 'none', // CODE: 32b8cfab: border-right: none here because responsive border will replace it
        },
      }}
      {...props}
    >
      {!isMobileDevice && canMinimize && isMinimized && (
        <MinimizedPanel title={title} subtitle={subtitle} handleOnMinimize={handleOnMinimize} />
      )}
      {!isMinimized && (
        <div
          sx={{
            display: isMinimized && !isMobileDevice ? 'none' : 'flex',
            flexFlow: 'column',
            height: '100%',
            position: 'relative',
          }}
        >
          {isLoading && <Loading />}
          <div
            onClick={isMobileDevice ? onClose : undefined}
            sx={{ flexShrink: 0, position: 'relative' }}
          >
            <FlexGroup
              alignItems="center"
              prefix={
                isMobileDevice && !!onClose && <BdIcon name="icon-chevron-left" color="deepSea30" />
              }
              suffix={
                <span sx={{ fontSize: sxtheme(t => t.misc.panelIconFontSize) }}>
                  {headerActions}
                  {!isMobileDevice && canMinimize && (
                    <IconButton
                      ariaLabel={`Minimize ${title} panel`}
                      data-testid="minimizeButton"
                      icon="icon-minimize"
                      color="sea100"
                      bold
                      tooltipText="Minimize panel"
                      size="medium"
                      onClick={handleOnMinimize}
                    />
                  )}
                  {!isMobileDevice && canClose && onClose && (
                    <IconButton
                      ariaLabel={`Close ${title} panel`}
                      data-testid="closeButton"
                      icon="icon-close"
                      color="sea100"
                      bold
                      tooltipText="Close panel"
                      size="medium"
                      onClick={onClose}
                    />
                  )}
                </span>
              }
              sx={{
                p: ['0px 16px 12px', '12px 16px'],
                ...(!(isSecondary === true && !isMobileDevice) && {
                  borderBottom: sxtheme(t => `1px solid ${t.colors.stone20}`),
                  bg: ['deepSea90', 'transparent'],
                  borderTop: [`1px solid ${theme.colors.deepSea90}`, 'none'],
                }),
              }}
            >
              {isMobileDevice && subtitleMobile && (
                <Typography variant="caption" color="deepSea30">
                  <Shiitake lines={1}>{subtitleMobile}</Shiitake>
                </Typography>
              )}
              <Typography variant="h5" color={['white', 'deepSea90']} inline>
                {title}
              </Typography>
            </FlexGroup>
          </div>
          <div sx={{ flexShrink: 0, position: 'relative' }}>
            {toolbar && !isLoading && <div sx={{ p: 0, flexShrink: 0 }}>{toolbar}</div>}
            {hasScrollToTopConfigured && isScrollToTopVisible && (
              <div
                sx={{
                  position: 'absolute',
                  bottom: -40,
                  zIndex: 5, // NOTE: would conflict with semantic dropdown (zIndex: 5)
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <ScrollToTopButton
                  onClick={() => {
                    refScrollCon && refScrollCon.current.scrollTo(0, 0);
                  }}
                />
              </div>
            )}
          </div>

          {!isLoading &&
            (hasError ? (
              <Error
                title="Something went wrong."
                subtitle="Please check your network connectivity and try again."
                linkText="Click here to retry"
                linkCallback={onRetryClick}
              />
            ) : (
              <div
                ref={refScrollCon}
                onScroll={e =>
                  hasScrollToTopConfigured &&
                  setisScrollToTopVisible(
                    (e.target as HTMLElement).scrollTop >= scrollToTopButtonThresh
                  )
                }
                sx={{
                  position: 'relative',
                  flexGrow: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  ...(flexContentDirection && {
                    display: 'flex',
                    flexDirection: flexContentDirection,
                  }),
                  ...(grayBG && {
                    bg: ['transparent', 'deepSea10'],
                  }),
                }}
              >
                {children}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

// =====================================================================================================================================

type MinimizedPanelProps = {
  title: string;
  subtitle: string;
  handleOnMinimize: () => void;
};

const MinimizedPanel: React.FC<MinimizedPanelProps> = ({
  title,
  subtitle = null,
  handleOnMinimize,
}) => {
  return (
    <div
      data-testid="minimizedPanel"
      data-sidebar={title}
      sx={{
        cursor: 'pointer',
        p: '12px',
        width: '100%',
        height: '100%',
        '&:hover': {
          bg: sxtheme(t => t.colors.stone10),
          transition: '0.5s all',
        },
      }}
      onClick={handleOnMinimize}
    >
      <BdIcon name="icon-panel-expand" color="sea90" bold nomargin />
      <div
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          transform: 'rotate(-90deg)',
          whiteSpace: 'nowrap',
          alignItems: 'center',
        }}
      >
        {!!subtitle && (
          <Typography
            sx={{ fontStyle: 'italic', marginRight: '8px' }}
            variant="body2"
            color="sea90"
          >
            {subtitle}
            &nbsp;&nbsp;/
          </Typography>
        )}
        <Typography variant="h6" color="stone90">
          {title}
        </Typography>
      </div>
    </div>
  );
};
